import { useEffect, useState } from 'react'
import { api, ApiError, codeWords, type Vendor, type VendorRequestStanding } from '../../lib/api'
import { vendorLifecycle } from '../../lib/lifecycle'
import { personLabel } from '../../lib/people'
import { requestFooter, statutoryNote, type RequestFooter } from '../../lib/requestRules'
import { ErrorBox, Loading } from '../DataTable'
import { Drawer } from '../kit'
import { dataHandled, vendorName } from './facts'

// The Approve request drawer (release 3.3, the 12 Oct plan section 2.3).
//
// # WHY THIS EXISTS
//
// A vendor is Requested until a TPRM lead approves the request, and no
// questionnaire can go to it before then (the server refuses, REQUEST_NOT_
// APPROVED). The lead decides here, with what they need in front of them and
// nothing else: what was requested, the tier and why (the tier chooses the
// questionnaire the vendor will get), what is still missing, and a reason.
// Approve needs no reason; Reject needs one and ends the request at Rejected.
//
// # TWO APPROVALS, NAMED APART
//
// This is "Approve request", the decision at the start. "Approve vendor" is the
// risk decision at the end, on the Profile, after due diligence (C3). The
// drawer never says "approve" alone.
//
// # NO COPY OF THE RULE
//
// Whether this reader may approve or reject, and why not, is the lifecycle
// read's answer (VendorRequestStanding, from domain.CheckRequestDecision on the
// server): read when the drawer opens, so a decision is made on the state as it
// is now. requestFooter (lib/requestRules.ts) only draws that answer, which is
// what the console test holds.

export function RequestDrawer({
  vendor,
  open,
  onClose,
  onDecided,
  names,
}: {
  vendor: Vendor
  open: boolean
  onClose: () => void
  // Told after a decision the API accepted, so the record reads itself again.
  onDecided: () => void
  names: Record<string, string>
}) {
  const [state, setState] = useState<VendorRequestStanding | null>(null)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [reason, setReason] = useState('')
  // Which decision is in flight, so only its own button says so and a screen
  // reader can tell which act is running (review of 12 Oct, N9).
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  useEffect(() => {
    if (!open) return
    let live = true
    setState(null)
    setLoadError(null)
    setError(null)
    vendorLifecycle(vendor.id)
      .then((l) => {
        // An API older than the request step reads no block: nothing to decide.
        if (live) setState(l.request ?? { approved: false, raisedBy: '', secondPersonApproves: false, legalFormKnown: true })
      })
      .catch((e: unknown) => {
        if (live) setLoadError(e)
      })
    return () => {
      live = false
    }
  }, [open, vendor.id])

  // Nothing is offered until the read says what this reader may do.
  const footer: RequestFooter = state ? requestFooter(state, reason, busy !== null) : { approve: { disabled: true }, reject: { disabled: true } }

  async function decide(kind: 'approve' | 'reject') {
    setBusy(kind)
    setError(null)
    try {
      if (kind === 'approve') await api.approveVendorRequest(vendor.id, reason.trim())
      else await api.rejectVendorRequest(vendor.id, reason.trim())
      setReason('')
      onClose()
      onDecided()
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, 'UNKNOWN', String(e)))
    } finally {
      setBusy(null)
    }
  }

  const data = dataHandled(vendor)
  const tier = vendor.tier
  const missing = statutoryNote(vendor)
  const raisedBy = state?.raisedBy ? personLabel(names, state.raisedBy) : ''

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Approve request"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy !== null}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void decide('reject')}
            disabled={footer.reject.disabled}
            title={footer.reject.why}
            data-action="tprm.vendor,vendor-record.a38"
          >
            {busy === 'reject' ? 'Rejecting' : 'Reject request'}
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => void decide('approve')}
            disabled={footer.approve.disabled}
            title={footer.approve.why}
            data-action="tprm.vendor,vendor-record.a37"
          >
            {busy === 'approve' ? 'Approving' : 'Approve request'}
          </button>
        </>
      }
    >
      {loadError != null && <ErrorBox error={loadError} what="The request" />}
      {loadError == null && state == null && <Loading what="the request" />}
      {error && <ErrorBox error={error} what="The decision" verb="recorded" />}

      <section className="vd-request" aria-label="What was requested">
        <h3>What was requested</h3>
        <dl className="vd-request-facts">
          <dt>Vendor</dt>
          <dd>
            {vendorName(vendor)} <span className="kit-mono">{vendor.vendorCode}</span>
          </dd>
          {vendor.vendorType && (
            <>
              <dt>Type</dt>
              <dd>{codeWords(vendor.vendorType)}</dd>
            </>
          )}
          {vendor.businessFunction && (
            <>
              <dt>For</dt>
              <dd>{vendor.businessFunction}</dd>
            </>
          )}
          {vendor.serviceDescription && (
            <>
              <dt>Service</dt>
              <dd>{vendor.serviceDescription}</dd>
            </>
          )}
          <dt>Data</dt>
          <dd>{data.full || 'None recorded'}</dd>
          {raisedBy && (
            <>
              <dt>Requested by</dt>
              <dd>{raisedBy}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="vd-request" aria-label="Tier">
        <h3>Tier</h3>
        {tier?.label ? (
          <p>
            <strong>{tier.label}</strong>
            {tier.why ? '. ' + tier.why : ''}
          </p>
        ) : (
          <p>
            Not set yet. The tier chooses the questionnaire this vendor gets.{' '}
            <a href={'#/vendor/' + vendor.id + '/full/risk'} onClick={onClose}>
              Set the tier
            </a>
          </p>
        )}
      </section>

      {missing && <p className="vd-quiet vd-request-note">{missing}</p>}

      <div className="field">
        <label htmlFor="rq-reason">Reason</label>
        <textarea
          id="rq-reason"
          rows={3}
          maxLength={1000}
          value={reason}
          aria-describedby="rq-reason-note"
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="sw-help" id="rq-reason-note">
          Optional to approve. Needed to reject.
        </p>
      </div>

      {footer.note && <p className="muted">{footer.note}</p>}
      {!footer.note && footer.approve.disabled && footer.approve.why && busy === null && <p className="muted">{footer.approve.why}</p>}
    </Drawer>
  )
}
